import type { Job, StripeSubscription, StripeSubscriptionPlan, User } from 'types';

import { logEventGTM } from './analyticsGTM';
import { STRIPE_SUBSCRIPTION_STATUS } from './constant';
import { GA4_EVENTS, PLAN_CATEGORIES } from './constants-v2';

export type StripeSubcriptionGA4Event = {
  product_id: string;
  price: number;
  currency: string;
  job_title?: string;
};

export type TCMJobTitleClickGA4Event = {
  userId: string;
  userGeoLocation: string;
  clientIP: string;
  userAgent: string;
  jobTitle: string;
};

export const transfortDataToLogJob = (job: Job) => ({
  job_id: job.jobId,
  job_type: job.cpc > 0 ? 'Paid' : 'Unpaid',
  partner_name: job.source,
  listing_hash: job.listingHash,
});

const getSubscriptionType = (plan: (typeof PLAN_CATEGORIES)[keyof typeof PLAN_CATEGORIES]) => {
  switch (plan) {
    case PLAN_CATEGORIES.EMPLOYER_JOB_POSTING:
      return 'post a job';
    case PLAN_CATEGORIES.COMPANY_PROFILE:
      return 'company profile';
    case PLAN_CATEGORIES.RESUME_BUILDER:
    default:
      return 'resume';
  }
};

export const gtm = {
  logDLPApplyClick(job: Job) {
    logEventGTM({
      event: GA4_EVENTS.DLP_APPLY_CLICK,
      ...transfortDataToLogJob(job),
    });
  },
  logCarouselJobClick(carousel_title: string, job: Job) {
    logEventGTM({
      event: GA4_EVENTS.CAROUSEL_JOB_CLICK,
      carousel_title,
      ...transfortDataToLogJob(job),
    });
  },
  logJobClick(job: Job) {
    logEventGTM({
      event: GA4_EVENTS.JOB_CLICK,
      ...transfortDataToLogJob(job),
    });
  },
  logBeginCheckout(
    category: (typeof PLAN_CATEGORIES)[keyof typeof PLAN_CATEGORIES],
    plan: StripeSubscriptionPlan
  ) {
    logEventGTM({
      event: GA4_EVENTS.BEGIN_CHECKOUT,
      subscription_type: getSubscriptionType(category),
      subscription_price: plan.PRICE,
      subscription_name: plan.TITLE,
    });
  },
  logLogin(login_type: string) {
    logEventGTM({
      event: GA4_EVENTS.LOGIN,
      login_type,
    });
  },
  logLoginFailed(error: string) {
    logEventGTM({
      event: GA4_EVENTS.LOGIN_FAILED,
      error,
    });
  },
  logSignUp(job_title: string) {
    logEventGTM({
      event: GA4_EVENTS.SIGN_UP,
      job_title,
    });
  },
  logSignUpFailed(error: string) {
    logEventGTM({
      event: GA4_EVENTS.SIGN_UP_FAILED,
      error,
    });
  },
  logJobFilterClick(filter_name: string) {
    logEventGTM({
      event: GA4_EVENTS.JOB_FILTER_CLICK,
      filter_name,
    });
  },
  logSalaryFilterClick(filter_name: string) {
    logEventGTM({
      event: GA4_EVENTS.SALARY_FILTER_CLICK,
      filter_name,
    });
  },
  logContentFilterClick(filter_name: string) {
    logEventGTM({
      event: GA4_EVENTS.CONTENT_FILTER_CLICK,
      filter_name,
    });
  },
  logSearch(search_term: string, search_term_normalized: string, search_source: string) {
    logEventGTM({
      event: GA4_EVENTS.SEARCH,
      search_term,
      search_term_normalized,
      search_source,
    });
  },
  logEasyApplySubmit(job: Job) {
    logEventGTM({
      event: GA4_EVENTS.DLP_APPLY_RESUME_SUBMITTED,
      ...transfortDataToLogJob(job),
    });
  },
  logRBCalloutClick(slug?: string) {
    logEventGTM({
      event: GA4_EVENTS.RB_CALLOUT_CLICK,
      slug,
    });
  },
  logJobCalloutClick(slug: string) {
    logEventGTM({
      event: GA4_EVENTS.JOB_CALLOUT_CLICK,
      slug,
    });
  },
  logCareerQuizCalloutClick({ slug, star_rating }: { slug?: string; star_rating?: number }) {
    logEventGTM({
      event: GA4_EVENTS.CAREER_QUIZ_CALLOUT_CLICK,
      slug,
      star_rating,
    });
  },
  logReviewsCalloutClick(star_rating: number) {
    logEventGTM({
      event: GA4_EVENTS.REVIEWS_CALLOUT_CLICK,
      star_rating,
    });
  },
  logSalaryReportCalloutClick(slug?: string) {
    logEventGTM({
      event: GA4_EVENTS.SALARY_REPORT_CALLOUT_CLICK,
      slug,
    });
  },
  logEasyApplyWithLinkedInClick(job: Job) {
    logEventGTM({
      event: GA4_EVENTS.EASY_APPLY_WITH_LINKEDIN_CLICK,
      ...transfortDataToLogJob(job),
    });
  },
  logCreateSubscription(data: StripeSubcriptionGA4Event) {
    logEventGTM({
      event: GA4_EVENTS.CREATE_SUBSCRIPTION,
      ...data,
    });
  },
  logCppInPageReview(slug: string) {
    logEventGTM({
      event: GA4_EVENTS.CPP_IN_PAGE_REVIEW,
      slug,
    });
  },
  logCareerCoachAdClick(test_group: string) {
    logEventGTM({
      event: GA4_EVENTS.CAREER_COACH_AD_CLICK,
      test_group,
    });
  },
  logSignUpTriggerDisplayed(signupSource: string) {
    logEventGTM({
      event: GA4_EVENTS.SIGN_UP_TRIGGER_DISPLAYED,
      signupSource,
    });
  },
  logSignUpTriggerSucceed(signupSource: string) {
    logEventGTM({
      event: GA4_EVENTS.SIGN_UP_TRIGGER_SUCCEED,
      signupSource,
    });
  },
  logCMJobTitleClick(data: TCMJobTitleClickGA4Event) {
    logEventGTM({
      event: GA4_EVENTS.CM_JOB_TITLE_CLICK,
      ...data,
    });
  },
};

export const logSubscriptionEvent = (subscription: StripeSubscription, user: User) => {
  const url = new URL(window.location.href);
  const subscriptionStatus = url.searchParams.get('subscription_status');

  if (subscriptionStatus === STRIPE_SUBSCRIPTION_STATUS.ACTIVE && subscription?.productId) {
    const title = user?.profileData?.find(({ name }) => name === 'titles')?.value?.[0]
      ?.normalizedTitle;

    const data = {
      product_id: subscription.productId,
      price: subscription.price,
      currency: subscription.currency,
    };

    if (title) {
      data['job_title'] = title;
    }

    gtm.logCreateSubscription(data);
  }

  url.searchParams.delete('subscription_status');
  window.history.replaceState({}, document.title, url.href);
};
